import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';
import { supportedLanguages, SupportedLanguage, getLocalizedPath } from '@/i18n/config';

interface SEOHeadProps {
  titleKey?: string;
  descriptionKey?: string;
  title?: string;
  description?: string;
  canonicalPath?: string;
  noIndex?: boolean;
  ogImage?: string;
  ogType?: 'website' | 'article';
  structuredData?: object;
}
const BASE_URL = 'https://flashysites.lovable.app';
const DEFAULT_OG_IMAGE = '/og-image.png';

export const SEOHead: React.FC<SEOHeadProps> = ({
  titleKey,
  descriptionKey,
  title: customTitle,
  description: customDescription,
  canonicalPath,
  noIndex = false,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  structuredData
}) => {
  const { currentLanguage, t } = useLanguage();
  
  // Get title and description from translation keys or custom values
  const title = customTitle || (titleKey ? t(titleKey) : 'FlashySites');
  const description = customDescription || (descriptionKey ? t(descriptionKey) : t('seo.home.description'));
  
  // Build canonical URL
  const currentPath = canonicalPath || '';
  const canonicalUrl = `${BASE_URL}/${currentLanguage}${currentPath ? `/${currentPath}` : '/'}`;
  
  // Build hreflang URLs for all supported languages
  const hreflangUrls = supportedLanguages.map((lang) => {
    const localizedPath = currentPath ? getLocalizedPath(lang, currentPath) : '';
    return {
      lang,
      url: `${BASE_URL}/${lang}${localizedPath ? `/${localizedPath}` : '/'}`
    };
  });
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`;
  const siteName = 'FlashySites';

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={currentLanguage} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="author" content="FlashySites" />
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta name="googlebot" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="theme-color" content="#030303" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang Tags for Language Alternates */}
      {hreflangUrls.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={hreflangUrls[0].url} />
      
      {/* Open Graph */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${title} - FlashySites`} />
      <meta property="og:locale" content={currentLanguage === 'es' ? 'es_ES' : 'en_US'} />
      {supportedLanguages.filter(l => l !== currentLanguage).map(lang => (
        <meta 
          key={lang} 
          property="og:locale:alternate" 
          content={lang === 'es' ? 'es_ES' : 'en_US'} 
        />
      ))}
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@flashysites" />
      <meta name="twitter:creator" content="@flashysites" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:image:alt" content={`${title} - FlashySites`} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Helper to generate Organization structured data
export const getOrganizationSchema = (lang: SupportedLanguage) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "FlashySites",
  "alternateName": "Flashy Sites",
  "description": lang === 'es' 
    ? "Sitios web profesionales en un flash. Servicio de diseño web por suscripción para restaurantes, salones, barberías y pequeños negocios."
    : "Professional websites in a flash. Subscription-based web design service for restaurants, salons, barbers, and small businesses.",
  "url": `${BASE_URL}/${lang}/`,
  "logo": {
    "@type": "ImageObject",
    "url": `${BASE_URL}/favicon.png`,
    "width": 512,
    "height": 512
  },
  "image": `${BASE_URL}/og-image.png`,
  "foundingDate": "2024",
  "slogan": lang === 'es' ? "Sitios Web Profesionales en un Flash" : "Professional Websites in a Flash",
  "knowsLanguage": ["en", "es"],
  "sameAs": []
});

// Helper to generate WebSite structured data with search action
export const getWebSiteSchema = (lang: SupportedLanguage) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "FlashySites",
  "alternateName": "Flashy Sites",
  "url": `${BASE_URL}/${lang}/`,
  "description": lang === 'es'
    ? "Obtén tu sitio web profesional para negocios en minutos con FlashySites."
    : "Get your professional business website live in minutes with FlashySites.",
  "inLanguage": lang === 'es' ? 'es-ES' : 'en-US',
  "publisher": {
    "@type": "Organization",
    "name": "FlashySites",
    "logo": {
      "@type": "ImageObject",
      "url": `${BASE_URL}/favicon.png`
    }
  }
});

// Helper to generate LocalBusiness structured data
export const getLocalBusinessSchema = (lang: SupportedLanguage) => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "FlashySites",
  "description": lang === 'es'
    ? "Servicio de diseño web por suscripción para pequeños negocios"
    : "Subscription-based web design service for small businesses",
  "url": `${BASE_URL}/${lang}/`,
  "logo": `${BASE_URL}/favicon.png`,
  "image": `${BASE_URL}/og-image.png`,
  "priceRange": "$100-$250",
  "serviceType": lang === 'es' ? "Diseño Web" : "Web Design",
  "areaServed": {
    "@type": "Country",
    "name": lang === 'es' ? "Estados Unidos" : "United States"
  }
});

// Helper to generate BreadcrumbList structured data
export const getBreadcrumbSchema = (
  lang: SupportedLanguage, 
  items: Array<{ name: string; url: string }>
) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

// Helper to generate FAQPage structured data for rich search results
export const getFAQSchema = (
  faqs: Array<{ question: string; answer: string }>
) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map((faq) => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

// Helper to generate AggregateRating with Reviews structured data
export const getReviewsSchema = (
  reviews: Array<{ author: string; reviewBody: string; rating: number }>,
  itemReviewed: { name: string; description: string; url: string }
) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": itemReviewed.name,
  "description": itemReviewed.description,
  "url": itemReviewed.url,
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1),
    "reviewCount": reviews.length,
    "bestRating": "5",
    "worstRating": "1"
  },
  "review": reviews.map((review) => ({
    "@type": "Review",
    "author": {
      "@type": "Person",
      "name": review.author
    },
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": review.rating,
      "bestRating": "5",
      "worstRating": "1"
    },
    "reviewBody": review.reviewBody
  }))
});

// Helper to generate Product structured data for pricing plans
export const getProductSchema = (
  plans: Array<{
    name: string;
    description: string;
    price: number;
    currency: string;
    billingPeriod?: 'month' | 'year' | 'one-time';
  }>,
  provider: { name: string; url: string }
) => plans.map(plan => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": plan.name,
  "description": plan.description,
  "brand": {
    "@type": "Brand",
    "name": provider.name
  },
  "offers": {
    "@type": "Offer",
    "url": provider.url,
    "priceCurrency": plan.currency,
    "price": plan.price,
    "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    "availability": "https://schema.org/InStock",
    ...(plan.billingPeriod && plan.billingPeriod !== 'one-time' && {
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": plan.price,
        "priceCurrency": plan.currency,
        "billingDuration": {
          "@type": "QuantitativeValue",
          "value": 1,
          "unitCode": plan.billingPeriod === 'month' ? 'MON' : 'ANN'
        }
      }
    })
  }
}));

// Helper to generate Service structured data
export const getServiceSchema = (
  services: Array<{
    name: string;
    description: string;
    provider: string;
    areaServed?: string;
    serviceType?: string;
  }>,
  providerInfo: { name: string; url: string }
) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "provider": {
    "@type": "Organization",
    "name": providerInfo.name,
    "url": providerInfo.url
  },
  "serviceType": "Web Design",
  "areaServed": {
    "@type": "Country",
    "name": "United States"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Web Design Services",
    "itemListElement": services.map((service, index) => ({
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": service.name,
        "description": service.description
      }
    }))
  }
});

// Helper to generate HowTo structured data for step-by-step guides
export const getHowToSchema = (
  name: string,
  description: string,
  steps: Array<{ name: string; text: string }>,
  totalTime?: string
) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": name,
  "description": description,
  ...(totalTime && { "totalTime": totalTime }),
  "step": steps.map((step, index) => ({
    "@type": "HowToStep",
    "position": index + 1,
    "name": step.name,
    "text": step.text
  }))
});

// Helper to generate SoftwareApplication structured data
export const getSoftwareApplicationSchema = (
  lang: 'en' | 'es',
  options?: {
    ratingValue?: number;
    ratingCount?: number;
  }
) => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "FlashySites",
  "description": lang === 'es'
    ? "Plataforma de diseño web por suscripción para pequeños negocios. Crea tu sitio web profesional en minutos."
    : "Subscription-based web design platform for small businesses. Create your professional website in minutes.",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web",
  "url": `${BASE_URL}/${lang}/`,
  "image": `${BASE_URL}/og-image.png`,
  "offers": {
    "@type": "AggregateOffer",
    "lowPrice": "100",
    "highPrice": "500",
    "priceCurrency": "USD",
    "offerCount": "3"
  },
  ...(options?.ratingValue && options?.ratingCount && {
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": options.ratingValue,
      "ratingCount": options.ratingCount,
      "bestRating": "5",
      "worstRating": "1"
    }
  }),
  "featureList": lang === 'es'
    ? ["Diseño responsivo", "SSL incluido", "SEO optimizado", "Soporte dedicado", "Actualizaciones ilimitadas"]
    : ["Responsive design", "SSL included", "SEO optimized", "Dedicated support", "Unlimited updates"]
});
